import { useTranslation } from "react-i18next";

/**
 * StepIndicator component displays the current step in a stepper form.
 *
 * @param currentStep - The current step number (1-based)
 * @param totalSteps - Total number of steps
 * @param stepLabels - Optional array of labels for each step
 */
interface StepIndicatorProps {
  currentStep: number;
  totalSteps?: number;
  stepLabels?: string[];
}

export function StepIndicator({ currentStep, totalSteps = 2, stepLabels }: StepIndicatorProps) {
  const { t } = useTranslation(["translation"]);

  const defaultLabels = [
    t("stepper.details"),
    t("stepper.linkservices"),
  ];

  const labels = stepLabels || defaultLabels;
  const steps = Array.from({ length: totalSteps }, (_, i) => i + 1);

  return (
    <div className="flex flex-col items-center mb-6">
      {/* Step circles and connector line */}
      <div className="flex items-center">
        {steps.map((step) => (
          <div key={step} className="flex items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                currentStep === step
                  ? "bg-primary text-primary-foreground"
                  : currentStep > step
                    ? "bg-green-500 text-white"
                    : "bg-muted text-muted-foreground"
              }`}
            >
              {currentStep > step ? "✓" : step}
            </div>
            {step < totalSteps && (
              <div
                className={`w-16 h-0.5 transition-colors ${
                  currentStep > step ? "bg-green-500" : "bg-muted"
                }`}
              />
            )}
          </div>
        ))}
      </div>
      {/* Step labels */}
      <div className="flex items-center mt-2">
        {steps.map((step) => (
          <div key={step} className="flex items-center">
            <div className="w-8 flex justify-center">
              {labels[step - 1] && (
                <span
                  className={`text-xs whitespace-nowrap px-3 ${
                    currentStep >= step ? "text-foreground" : "text-muted-foreground"
                  }`}
                >
                  {labels[step - 1]}
                </span>
              )}
            </div>
            {step < totalSteps && <div className="w-20" />}
          </div>
        ))}
      </div>
    </div>
  );
}

export default StepIndicator;
